/**
 * Partner profile: photo, country, output over time, recent newsletters.
 *
 * The chart of updates per year is a way in as well as a reading: picking a year lists
 * that year's updates in the card below, in place of the twenty-five most recent. The
 * bar and the picker beside the list are the same control — see UpdatesCard.
 */
import { useState, useMemo, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { PageHeader } from '../App'
import { useApi, qs } from '../lib/api'
import { BarsChart } from '../components/charts'
import { MediaImage, MediaLightbox } from '../components/ImageUpload'
import { PartnerProfileCard } from '../components/PartnerProfileCard'
import { YearSelect } from '../components/Select'
import {
  Card, Badge, Empty, Loading, ErrorState, BackTo, PartnerLabel, SearchInput,
  fmtInt, fmtKb, relDays,
} from '../components/ui'
import { Icon } from '../components/Icon'
import { useCanBrowse } from '../lib/viewmode'
import { useCode } from '../lib/terms'
import { fundTypeLabel } from '../../shared/fundTypes.js'

/** Labelled the way the sidebar and the list's own header label the route. */
const Back = () => <BackTo to="/partners">Partner Units</BackTo>

/**
 * How many of a year's updates the drill-down asks for. No partner unit has sent
 * anything like this many in one year — the busiest in the archive manages a dozen —
 * but the card counts what it was given against the total and says so if a year ever
 * runs past it, rather than quietly showing part of one.
 */
const YEAR_LIMIT = 200

export default function PartnerDetail({ user }) {
  const code = useCode()
  const { id } = useParams()
  const canBrowse = useCanBrowse()
  const query = useApi(['partner', id], `partners/${id}`)
  const [viewing, setViewing] = useState(null)
  const [year, setYear] = useState(null)

  /* A picked year belongs to the unit it was picked on. Nothing in the app links one
     record straight to another today, so this route always remounts between units and
     the state would clear itself — but it is the id that owns the selection, not the
     absence of such a link, and 2015 arriving on a unit that has nothing in 2015 would
     read as a broken page. Adjusted while rendering rather than in an effect, the way
     the report's tag strip is, so it is never painted in the wrong state for a frame. */
  const [seenId, setSeenId] = useState(id)
  if (seenId !== id) {
    setSeenId(id)
    setYear(null)
  }

  /* This unit's updates for one year, in the same order as the recent list the card
     otherwise shows. Alumni are included deliberately: everywhere else that default
     answers "whose updates does the archive report on", but here the question has
     already been answered by the record being open — and the chart above counts an
     alumni unit's updates too, so filtering them out would list nothing under a bar
     that says eight. */
  const yearQuery = useApi(
    ['partner-year-updates', id, year],
    `newsletters${qs({
      partner_id: id,
      year,
      include_alumni: 1,
      limit: YEAR_LIMIT,
      sort: 'published_on',
      dir: 'desc',
    })}`,
    { enabled: !!year }
  )

  /* The years this unit actually has updates in — the bars of the chart, in the same
     order — so the picker beside the list offers the answers the chart does and no
     empty ones. Strings, because that is what the chart's x values are. */
  const years = useMemo(
    () => (query.data?.byYear ?? []).map((r) => String(r.year)),
    [query.data]
  )

  // Spelled out rather than left to QueryState, which renders its states bare:
  // the way back has to survive a record that is still loading or will not load,
  // and a broken id is exactly when the reader most needs it.
  if (query.isLoading) {
    return (
      <PageHeader title="Partner Unit" back={<Back />}>
        <Loading />
      </PageHeader>
    )
  }
  if (query.isError) {
    return (
      <PageHeader title="Partner Unit" back={<Back />}>
        <ErrorState error={query.error} retry={query.refetch} />
      </PageHeader>
    )
  }

  const d = query.data
  const p = d.partner
  const photo = d.images?.find((i) => i.id === p.photo_media_id) ?? d.images?.[0]
  return (
    <PageHeader
      title={`${code(p.s4ca_code)} ${p.display_name}`}
      sub={p.country_name ?? 'No country recorded'}
      back={<Back />}
      actions={
        <>
          {/* Staff only. The report it opens is not part of a view-only account's view,
              and neither is the endpoint behind it. */}
          {canBrowse && p.newsletter_count > 0 && (
            <Link className="btn btn-sm" to={`/reports/newsletters-by-partner?partner=${p.partner_id}`}>
              Review Updates
            </Link>
          )}
          {user.caps.update && (
            <Link className="btn btn-sm" to={`/partners/${id}/edit`}>
              Edit
            </Link>
          )}
          {/* Opens the form with this unit already chosen — see `?partner=` there. */}
          {user.caps.create && (
            <Link className="btn btn-sm btn-primary" to={`/newsletters/new?partner=${p.partner_id}`}>
              + New Update
            </Link>
          )}
        </>
      }
    >
      <div className="report-layout">
        {/* Shared with the updates report, which shows the same unit beside its updates. */}
        <PartnerProfileCard partner={p} photo={photo} />

        <div className="col">
          {p.description ? (
            <Card title="Description">
              <p style={{ whiteSpace: 'pre-wrap' }}>{p.description}</p>
            </Card>
          ) : null}

          {d.byYear?.length ? (
            <Card title="Updates per Year" sub="Pick a year to list its updates below">
              <BarsChart
                data={d.byYear}
                x="year"
                series={[{ key: 'n', label: 'Updates' }]}
                height={200}
                /* Picking the year already showing puts the list back to the recent
                   twenty-five, so the bar behaves like the toggle it looks like. */
                onPick={(picked) => setYear((cur) => (cur === picked ? null : picked))}
                activeX={year}
              />
            </Card>
          ) : null}

          {/* Keyed on the record, so a search typed on one unit is not waiting in the
              box on the next. */}
          <UpdatesCard
            key={id}
            partner={p}
            recent={d.recent}
            years={years}
            year={year}
            onYear={setYear}
            query={yearQuery}
          />

          <LinkedUpdatesCard partner={p} rows={d.linked} total={d.linkedTotal ?? 0} />

          {d.images?.length ? (
            <Card title="Images" sub="Click an image to see it full size">
              <div className="media-grid">
                {d.images.map((img) => (
                  <button
                    className="media-item media-item-button"
                    key={img.id}
                    onClick={() => setViewing(img)}
                    title={img.alt_text || img.filename || 'View full size'}
                  >
                    <MediaImage id={img.id} alt={img.alt_text} />
                    <div className="media-meta subtle">{fmtKb(img.bytes)}</div>
                  </button>
                ))}
              </div>
            </Card>
          ) : null}
        </div>
      </div>

      <MediaLightbox image={viewing} onClose={() => setViewing(null)} />
    </PageHeader>
  )
}

/**
 * The unit's updates: the twenty-five most recent, or one year in full once a year is
 * picked. The chart above and the picker in this header set the same selection, so the
 * bar is a shortcut to the control rather than a second way of filtering — and the
 * control is why the chart may be mouse-only, since a select is reachable from the
 * keyboard and clearable from it too.
 *
 * The rows come from two endpoints — `recent` off the record, the year from the update
 * list — which send the same fields for a row of this table, so one table serves both
 * and clearing a year does not land the reader on a differently shaped list.
 */
function UpdatesCard({ partner: p, recent, years, year, onYear, query: yearQuery }) {
  const canBrowse = useCanBrowse()
  const kind = fundTypeLabel(p.fund_type).toLowerCase()

  /* A search of this record's own updates, and only its own: what it says, never its
     code or names, which every update here shares (`search_in=text`). Debounced like the
     archive-wide box. It narrows the year when one is picked, so the two controls
     combine rather than one silently replacing the other. */
  const [input, setInput] = useState('')
  const [term, setTerm] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setTerm(input.trim()), 300)
    return () => clearTimeout(t)
  }, [input])
  const searching = term.length > 1
  const searchQuery = useApi(
    ['partner-search-updates', p.partner_id, term, year],
    `newsletters${qs({
      partner_id: p.partner_id,
      q: term,
      search_in: 'text',
      year,
      include_alumni: 1,
      limit: YEAR_LIMIT,
      sort: 'published_on',
      dir: 'desc',
    })}`,
    { enabled: searching }
  )

  const query = searching ? searchQuery : yearQuery
  const listed = searching || !!year
  const rows = listed ? (query.data?.rows ?? []) : (recent ?? [])
  const total = query.data?.total ?? 0
  const inYear = year ? ` in ${year}` : ''

  const sub = () => {
    if (!listed) return p.newsletter_count > 25 ? `Showing 25 of ${fmtInt(p.newsletter_count)}` : undefined
    if (query.isLoading) return searching ? 'Searching…' : `Reading ${year}…`
    if (query.isError) return undefined
    const noun = searching ? `mentioning “${term}”` : ''
    if (total > rows.length) {
      return `${fmtInt(total)} ${noun}${inYear} — showing the first ${fmtInt(rows.length)}`
    }
    return `${fmtInt(total)} update${total === 1 ? '' : 's'} ${noun}${inYear}`.replace(/\s+/g, ' ')
  }

  const title = searching ? `Updates Mentioning “${term}”${inYear}` : year ? `Updates in ${year}` : 'Recent Updates'

  return (
    <Card
      title={title}
      sub={sub()}
      actions={
        <>
          {/* Only offered to a unit with more than one year on file: with one, the
              chart has a single bar and this would be a choice between it and itself. */}
          {years.length > 1 ? (
            <div style={{ width: 150 }}>
              <YearSelect
                inputId="pu-year"
                years={years}
                value={year}
                onChange={onYear}
                placeholder="Any year"
              />
            </div>
          ) : null}
          {canBrowse && p.newsletter_count > 0 && (
            <Link className="btn btn-sm" to={`/reports/newsletters-by-partner?partner=${p.partner_id}`}>
              See all
            </Link>
          )}
        </>
      }
    >
      {p.newsletter_count > 0 && (
        <div style={{ marginBottom: 12 }}>
          <SearchInput
            value={input}
            onChange={setInput}
            placeholder={`Search this ${kind}'s updates…`}
            aria-label={`Search this ${kind}'s updates`}
          />
        </div>
      )}
      {listed && query.isLoading ? (
        <Loading label={searching ? 'Searching…' : `Loading ${year}…`} />
      ) : listed && query.isError ? (
        <ErrorState error={query.error} retry={query.refetch} />
      ) : rows.length ? (
        <div className="table-wrap flush">
          <table className="data compact">
            <thead>
              <tr>
                <th>Date</th>
                <th>Country</th>
                <th>Text</th>
                <th className="right">Words</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="nowrap">
                    <Link className="mono" to={`/newsletters/${r.id}`}>
                      {r.published_on}
                    </Link>
                    <div className="tiny subtle">{relDays(r.published_on)}</div>
                  </td>
                  <td className="small">{r.country_name}</td>
                  <td className="cell-snippet-cell">
                    <div className="cell-snippet">{r.snippet}</div>
                  </td>
                  <td className="num">{fmtInt(r.word_count)}</td>
                  <td>
                    <span className="row row-tight">
                      {r.highlighted_need ? <Badge tone="warn"><Icon name="star" size={12} /></Badge> : null}
                      {r.has_prayer_request ? <Badge tone="accent"><Icon name="prayer" size={12} /></Badge> : null}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Empty
          icon="mail"
          title={
            searching
              ? `No updates from this ${kind} mention “${term}”${inYear}`
              : year
                ? `No updates recorded in ${year}`
                : `No updates recorded for this ${kind}`
          }
        />
      )}
    </Card>
  )
}

/**
 * The updates somebody *else* wrote that name this record.
 *
 * A partner unit reports on its own ministry and, often enough, on the training school it
 * teaches at or the project it runs. Since migration 0032 the update carries both codes,
 * and this is the half of that the school or the project sees: the card is the answer to
 * "what has been written about us", which until now nothing in the archive could ask.
 *
 * TWO CARDS, NOT ONE. The list above is what this record wrote; this is what was written
 * about it. Merging them would put a partner unit's byline in a list headed "Recent
 * updates" on a training school's page and make it look like the school's own — and the
 * count beside that heading, which every chart and report in the archive also counts,
 * would quietly start meaning something else. So they sit apart and each says whose it is.
 *
 * THE WAY BACK is the column that matters. A reader on the school's page is looking at an
 * update the school did not write; the unit that did is the next thing they want, so the
 * code is a link to that record rather than a label. A partner unit gets the same card for
 * the updates a school or a project wrote about it. Hidden when nothing links here, which
 * is most records of every kind.
 */
function LinkedUpdatesCard({ partner: p, rows, total }) {
  if (!rows?.length) return null

  return (
    <Card
      title={`Updates About This ${fundTypeLabel(p.fund_type)}`}
      sub={
        total > rows.length
          ? `Written by other records — showing ${fmtInt(rows.length)} of ${fmtInt(total)}`
          : `Written by other records — ${fmtInt(total)} update${total === 1 ? '' : 's'}`
      }
    >
      <div className="table-wrap flush">
        <table className="data compact">
          <thead>
            <tr>
              <th>Date</th>
              <th>From</th>
              <th>Country</th>
              <th>Text</th>
              <th className="right">Words</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="nowrap">
                  <Link className="mono" to={`/newsletters/${r.id}`}>
                    {r.published_on}
                  </Link>
                  <div className="tiny subtle">{relDays(r.published_on)}</div>
                </td>
                {/* The link back. `nowrap` for the same reason the Updates list has it:
                    without it the browser squeezes this column to its longest word and
                    "S4CA-430 G and T" becomes four lines. */}
                <td className="nowrap small">
                  <PartnerLabel id={r.partner_id} code={r.s4ca_code} name={r.partner_name} />
                </td>
                <td className="small">{r.country_name}</td>
                <td className="cell-snippet-cell">
                  <div className="cell-snippet">{r.snippet}</div>
                </td>
                <td className="num">{fmtInt(r.word_count)}</td>
                <td>
                  <span className="row row-tight">
                    {r.highlighted_need ? <Badge tone="warn"><Icon name="star" size={12} /></Badge> : null}
                    {r.has_prayer_request ? <Badge tone="accent"><Icon name="prayer" size={12} /></Badge> : null}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}
